import { BehaviorSubject } from 'rxjs';

export class Viewport<D> {
   public scrolled = 0;
   private rows = new Rows<D>(0);
   private steps = 0;
   readonly scrollStep = 100;
   private stepBorder = 0.999;
   public prevDirection: 1 | 0 = 1;
   private shaperDom: HTMLElement;
   private host: HTMLElement;
   public paginator: Paginator | null = null;
   private visibleCount = 0;

   constructor(
      private maxVisibleCount: number,
   ) { 
   }


   public setHost(host: HTMLElement, rowHeight: number, excludeHeight = 0) {
      const shaper = document.createElement('section');

      this.host = host;
      shaper.style.height = `0px`;
      shaper.style.width = `1px`;
      shaper.style.position = `absolute`;
      shaper.style.top = `100%`;

      this.shaperDom = this.host.appendChild(shaper);
      this.host.addEventListener('scroll', () => this.update());

      const left = host.offsetHeight - excludeHeight;

      this.visibleCount = Math.floor(left / rowHeight);

     if (this.visibleCount > this.maxVisibleCount || left <= rowHeight) {
         this.visibleCount = this.maxVisibleCount;
      }
      
      host.style.maxHeight = excludeHeight + this.visibleCount * rowHeight + 'px';
   }


   public build(rows: D[]): D[] {
      const { start, range } = this.getOffset();

      this.rows = new Rows<D>(rows.length);
      this.rows.all = rows;
      this.rows.up = rows.slice(0, start);
      this.rows.visible = rows.slice(start, range);
      this.rows.down = rows.slice(range).reverse();

      const shaperHeight = (rows.length - this.visibleCount) * this.scrollStep;

      this.shaperDom.style.height = (shaperHeight < 0 ? 0 : shaperHeight) + 'px';

      return this.rows.visible;
   }


   public setPaginator(paginator: PaginatorArgs) {
      this.paginator = new Paginator(paginator);
   }


   private getOffset() {
      let start = 0;
      let range = this.visibleCount;

      if (this.paginator !== null && this.paginator.isActive) {
         start = Math.floor(this.scrolled / this.scrollStep);
         range = this.visibleCount + start;
         this.paginator.isPaginating.next(false);
      } else {
         this.scrolled = 0;
         this.goTo(0);
      }

      return {
         start, range
      }
   }


   public update() {
      const scrollTop = this.host.scrollTop;
      const direction = this.scrolled < scrollTop ? 1 : 0;
      const { rows } = this;

      if (direction !== this.prevDirection) {
         this.steps = -this.steps;
      }

      this.prevDirection = direction;
      this.steps += Math.abs(scrollTop - this.scrolled) / this.scrollStep;
      this.scrolled = scrollTop;

      if (direction) {
         while (this.steps >= this.stepBorder && rows.down.length) {
            rows.up.push(rows.visible.shift());
            rows.visible.push(rows.down.pop());
            this.steps--;
         }

         if (this.paginator !== null) {
            this.paginator.tryToPaginate(rows.down.length, rows.all.length)
               .then(isEnd => {
                  if (isEnd) {
                     this.paginator = null;
                  }
               });
         }
      } else {
         while (this.steps >= this.stepBorder && rows.up.length) {
            rows.down.push(rows.visible.pop());
            rows.visible.unshift(rows.up.pop());
            this.steps--;
         }
      }
   }


   private goTo(scroll: number) {
      this.host.scrollTo({ top: scroll });
   }


   public goToIndex(index: number) {
      this.goTo(index * this.scrollStep);
   }


   public goToField(getter: (row: D) => boolean) {
      const index = this.rows.all.findIndex(getter);

      if (index !== -1) {
         this.goToIndex(index);
      }
   }
}

class Rows<D> {
   public all: D[] = new Array(this.size);
   public visible: D[] = new Array(this.size);
   public up: D[] = new Array(this.size);
   public down: D[] = new Array(this.size);

   constructor(private size: number) { }
}

// ================ Paginator ================
export interface PaginatorArgs {
   fireAfterRowsLeft: number;
   paginate: (rowsCount: number) => Promise<boolean>;
}


export class Paginator implements PaginatorArgs {
   readonly isPaginating = new BehaviorSubject(false);
   readonly fireAfterRowsLeft: number;
   readonly paginate: (rowsCount: number) => Promise<boolean>;

   get isActive(): boolean { return this.isPaginating.getValue() }

   constructor({ fireAfterRowsLeft, paginate }: PaginatorArgs) {
      this.fireAfterRowsLeft = fireAfterRowsLeft;
      this.paginate = paginate;
   }


   public async tryToPaginate(left: number, currentCount: number) {
      const isReachBorder = left <= this.fireAfterRowsLeft;
      const isNotInPagination = !this.isActive;

      // console.log({ left, after: this.fireAfterRowsLeft, currentCount, isReachBorder, isNotInPagination })

      if (isReachBorder && isNotInPagination) {
         this.isPaginating.next(true);
         return this.paginate(currentCount);
      } else {
         return Promise.resolve(false);
      }
   }
}